import React, { useState, useEffect } from 'react';
import { ChevronRight, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import dealsApi, { DealOverview } from '../../api/dealsApi';
import { useBusiness } from '../../context/BusinessContext';

const Overview: React.FC = () => {
    const [noteInput, setNoteInput] = useState('');
    const [data, setData] = useState<DealOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { selected } = useBusiness();
    const businessSlug = selected?.slug;

    useEffect(() => {
        if (!businessSlug) return;
        fetchData();
    }, [businessSlug]);

    const fetchData = async () => {
        if (!businessSlug) return;
        setLoading(true);
        try {
            const overview = await dealsApi.getOverview(businessSlug);
            setData(overview);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch overview:", err);
            setError("Failed to load overview data.");
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteInput.trim() || !businessSlug) return;
        try {
            await dealsApi.addNote(businessSlug, noteInput);
            setNoteInput('');
            // Refresh data to show new note
            const overview = await dealsApi.getOverview(businessSlug);
            setData(overview);
        } catch (err) {
            console.error("Failed to add note:", err);
            alert("Failed to add note.");
        }
    };

    if (loading && !data) {
        return <div className="flex h-full items-center justify-center text-gray-400"><Loader2 className="animate-spin w-8 h-8" /></div>;
    }

    if (error || !data) {
        return <div className="flex h-full items-center justify-center text-red-500">{error || "Select a business to view details"}</div>;
    }

    const { metrics, strengths, weaknesses, notes } = data;

    const metricsList = [
        { label: 'Monthly Deposits (Proxy)', value: `$${metrics.monthly_deposits.toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
        { label: 'Net Cash Flow', value: `$${metrics.net_cash_flow.toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
        { label: 'Cash Coverage', value: metrics.cash_coverage },
        { label: 'Volatility', value: metrics.volatility },
    ];

    return (
        <div className="flex flex-col gap-6 h-full overflow-y-auto scrollbar-hide pr-1 -mr-1">

            {/* Metrics Grid */}
            <div className="grid grid-cols-4 gap-4">
                {metricsList.map((m, i) => (
                    <div key={i} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col gap-2">
                        <h3 className="text-sm font-semibold text-gray-500">{m.label}</h3>
                        <div className="text-2xl font-bold text-gray-900">{m.value}</div>
                    </div>
                ))}
            </div>

            {/* Strengths & Weaknesses */}
            <div className="grid grid-cols-2 gap-4">
                {/* Strengths */}
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-gray-500" />
                        <h3 className="text-sm font-bold text-gray-900">Strengths</h3>
                    </div>
                    <ul className="space-y-2">
                        {strengths.length > 0 ? strengths.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
                                {s}
                            </li>
                        )) : <li className="text-sm text-gray-400">No specific strengths identified.</li>}
                    </ul>
                </div>

                {/* Weaknesses */}
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingDown className="w-4 h-4 text-gray-500" />
                        <h3 className="text-sm font-bold text-gray-900">Weaknesses</h3>
                    </div>
                    <ul className="space-y-2">
                        {weaknesses.length > 0 ? weaknesses.map((w, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
                                {w}
                            </li>
                        )) : <li className="text-sm text-gray-400">No specific weaknesses identified.</li>}
                    </ul>
                </div>
            </div>

            {/* Notes Section */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-sm font-bold text-gray-900">Notes</h3>
                    <button className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                        All notes
                        <ChevronRight className="w-3 h-3" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {notes.length > 0 ? notes.map((note, i) => (
                        <div key={i} className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0 overflow-hidden">
                                {note.avatar ? <img src={note.avatar} alt={note.author} /> : <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-500 text-xs font-bold">{note.author[0]}</div>}
                            </div>
                            <div>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-sm font-bold text-gray-900">{note.author}</span>
                                    <span className="text-xs text-gray-400">{note.time}</span>
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed">{note.text}</p>
                            </div>
                        </div>
                    )) : <div className="text-sm text-gray-400">No notes yet.</div>}
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-lg m-4 mt-0">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Add a note..."
                            className="flex-1 text-sm bg-transparent border-none focus:ring-0 placeholder:text-gray-400 outline-none"
                            value={noteInput}
                            onChange={(e) => setNoteInput(e.target.value)}
                        />
                        <button
                            onClick={handleAddNote}
                            className="px-3 py-1 bg-gray-300 text-white text-xs font-bold rounded hover:bg-gray-400 transition-colors"
                        >
                            Add +
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Overview;
